import React, { useState } from "react";
import { motion } from "framer-motion";
import { Bold, Italic, Underline, Strikethrough, Heading1, Heading2, List, ListOrdered, Quote, Code, Link2, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, Save, X, Eye, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Home", "Insert", "Layout", "References", "Review", "View"];

const TOOLS = {
  Home: [
    [{ Icon: Bold, label: "Bold" }, { Icon: Italic, label: "Italic" }, { Icon: Underline, label: "Underline" }, { Icon: Strikethrough, label: "Strike" }],
    [{ Icon: Heading1, label: "H1" }, { Icon: Heading2, label: "H2" }, { Icon: List, label: "List" }, { Icon: ListOrdered, label: "Ordered" }],
    [{ Icon: AlignLeft, label: "Left" }, { Icon: AlignCenter, label: "Center" }, { Icon: AlignRight, label: "Right" }],
    [{ Icon: Quote, label: "Quote" }, { Icon: Code, label: "Code" }],
  ],
  Insert: [
    [{ Icon: ImageIcon, label: "Image" }, { Icon: Link2, label: "Link" }, { Icon: Code, label: "Embed" }],
  ],
  Layout: [],
  References: [],
  Review: [],
  View: [],
};

export default function WordEditor({ initial, onClose }) {
  const [tab, setTab] = useState("Home");
  const [title, setTitle] = useState(initial?.title || "Untitled");
  const [body, setBody] = useState(initial?.body || "");

  return (
    <div className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
      {/* title bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-secondary/50 border-b border-border/60">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-rose-500/80" />
            <span className="h-3 w-3 rounded-full bg-amber-400/80" />
            <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
          </div>
          <span className="ml-3 font-mono text-[11px] text-muted-foreground">{title} — fmi.docx</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary/60" title="Undo"><Undo2 className="h-3.5 w-3.5" /></button>
          <button className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary/60" title="Redo"><Redo2 className="h-3.5 w-3.5" /></button>
          <button onClick={() => toast.success("Saved (mock)")} className="inline-flex items-center gap-1.5 h-7 px-3 rounded bg-primary text-primary-foreground text-[11px] font-mono uppercase tracking-[0.2em]"><Save className="h-3.5 w-3.5" /> Save</button>
          <button className="inline-flex items-center gap-1.5 h-7 px-3 rounded border border-border/60 text-[11px] font-mono uppercase tracking-[0.2em]"><Eye className="h-3.5 w-3.5" /> Preview</button>
          <button onClick={onClose} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary/60"><X className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-1 px-3 pt-2 bg-secondary/30">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-1.5 word-ribbon-tab uppercase tracking-[0.18em] rounded-t border-x border-t ${tab === t ? "bg-card border-border/60 text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{t}</button>
        ))}
      </div>

      {/* ribbon */}
      <div className="bg-card border-t border-border/60 px-4 py-3 flex items-center gap-4 flex-wrap">
        {(TOOLS[tab] || []).map((group, gi) => (
          <React.Fragment key={gi}>
            <div className="flex items-center gap-1">
              {group.map(({ Icon, label }) => (
                <button key={label} title={label} className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-secondary/60" onClick={() => toast(label + " applied (mock)")}>
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
            {gi < (TOOLS[tab].length - 1) && <span className="h-7 w-px bg-border/60" />}
          </React.Fragment>
        ))}
        {(TOOLS[tab] || []).length === 0 && <span className="text-[12px] text-muted-foreground">No tools in this tab — placeholder.</span>}
      </div>

      {/* page */}
      <div className="bg-[#0a0a0f] p-10">
        <motion.div layout className="mx-auto bg-card border border-border/70 shadow-2xl rounded-md p-12 min-h-[640px] max-w-3xl">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full bg-transparent text-3xl md:text-4xl font-medium tracking-display outline-none mb-6" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Start writing…" className="w-full bg-transparent outline-none text-[15px] leading-[1.75] text-foreground/85 min-h-[480px] resize-none" />
        </motion.div>
      </div>

      {/* status */}
      <div className="flex items-center justify-between px-4 py-2 bg-secondary/40 border-t border-border/60 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        <span>Page 1 of 1</span>
        <span>{body.split(/\s+/).filter(Boolean).length} words</span>
        <span>100%</span>
      </div>
    </div>
  );
}
